import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCategoriesPublic } from "@/hooks/useCategoriesPublic";
import { useBrandSettings } from "@/hooks/useBrandSettings";
import { allCategories, allTags } from "@/mocks/articles";
import Logo from "./Logo";

/* ─────────────────────────────────────────
   Hamburger / Side-Panel Menu
───────────────────────────────────────── */
interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  navItems: { label: string; path: string }[];
}

function SidePanel({ open, onClose, navItems }: SidePanelProps) {
  const location = useLocation();

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-[70] w-[300px] max-w-[85vw] bg-white flex flex-col
          transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-hj-gray-border">
          <Logo variant="header" />
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer"
            aria-label="Menü schließen"
          >
            <i className="ri-close-line text-xl text-hj-dark"></i>
          </button>
        </div>

        {/* User actions */}
        <div className="px-5 py-4 border-b border-hj-gray-border space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-hj-muted mb-3 font-body">
            Konto
          </p>
          <Link
            to="/admin/login"
            onClick={onClose}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-hj-dark hover:bg-hj-gray-light transition-colors font-body text-sm font-medium cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-red/10 text-hj-red">
              <i className="ri-login-box-line text-base"></i>
            </div>
            Anmelden
          </Link>
          <Link
            to="/admin/register"
            onClick={onClose}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-hj-dark hover:bg-hj-gray-light transition-colors font-body text-sm font-medium cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-dark/10 text-hj-dark">
              <i className="ri-user-add-line text-base"></i>
            </div>
            Registrieren
          </Link>
          <Link
            to="/admin"
            onClick={onClose}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-hj-dark hover:bg-hj-gray-light transition-colors font-body text-sm font-medium cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-navy/10 text-hj-navy">
              <i className="ri-user-line text-base"></i>
            </div>
            Profil / Admin
          </Link>
          {/* Dark mode placeholder */}
          <button
            disabled
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-hj-muted cursor-not-allowed font-body text-sm font-medium w-full text-left opacity-60"
            title="Demnächst verfügbar"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-muted/10 text-hj-muted">
              <i className="ri-moon-line text-base"></i>
            </div>
            <span>Dunkelmodus</span>
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-hj-muted bg-hj-gray-light px-2 py-0.5 rounded-full">
              Bald
            </span>
          </button>
        </div>

        {/* Navigation links */}
        <nav className="flex-1 overflow-y-auto px-4 py-3" aria-label="Mobile Navigation">
          <p className="text-[10px] font-bold uppercase tracking-widest text-hj-muted mb-3 px-2 font-body">
            Kategorien
          </p>
          <div className="space-y-0.5">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? "text-hj-red bg-hj-red/5 font-semibold"
                    : "text-hj-dark hover:bg-hj-gray-light"
                }`}
              >
                {item.label}
                {location.pathname === item.path && (
                  <i className="ri-arrow-right-line text-xs ml-auto text-hj-red"></i>
                )}
              </Link>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-hj-gray-border">
          <Link
            to="/kontakt"
            onClick={onClose}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-hj-dark bg-hj-gray-light rounded-lg hover:bg-hj-gray-border transition-colors"
          >
            <i className="ri-mail-line text-hj-red"></i>
            Kontakt
          </Link>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────
   Main Navbar
───────────────────────────────────────── */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const { menuCategories, loading: catsLoading } = useCategoriesPublic();
  const { settings } = useBrandSettings();

  const navItems = menuCategories.length > 0
    ? menuCategories.map((c) => ({ label: c.name, path: `/${c.slug}` }))
    : [
        { label: "Schweiz", path: "/schweiz" },
        { label: "Finanzen", path: "/finanzen" },
        { label: "Versicherung", path: "/versicherung-gesundheit" },
        { label: "Technologie", path: "/technologie" },
        { label: "Digital & KI", path: "/digital-ki" },
        { label: "Arbeit & Leben", path: "/arbeit-leben" },
        { label: "Politik", path: "/politik" },
        { label: "Deals", path: "/deals-angebote" },
        { label: "Meinung", path: "/meinung" },
        { label: "Kolumnen", path: "/kolumnen" },
      ];

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close everything on route change
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu/search is open
  useEffect(() => {
    document.body.style.overflow = menuOpen || searchOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen, searchOpen]);

  // Auto-focus search
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/suche?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-[0_1px_0_0_#E2E8F0]"
            : "bg-white"
        }`}
      >
        {/* ─────────────────────────────────────────────
            EXPANDED STATE (top of page, desktop)
            Logo centered + date + categories row below
        ───────────────────────────────────────────── */}
        <div
          className={`hidden lg:block overflow-hidden transition-all duration-500 ease-in-out ${
            scrolled ? "max-h-0 opacity-0" : "max-h-40 opacity-100"
          }`}
          aria-hidden={scrolled}
        >
          {/* Top bar: date left | logo center | actions right */}
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="grid grid-cols-3 items-center">
              {/* Date */}
              <div className="text-left">
                <p className="text-xs text-hj-muted font-body uppercase tracking-widest">
                  {new Date().toLocaleDateString("de-CH", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>

              {/* Centered logo */}
              <div className="flex justify-center">
                <Logo variant="header" />
              </div>

              {/* Right actions */}
              <div className="flex items-center justify-end gap-1">
                <button
                  onClick={() => setSearchOpen(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer"
                  aria-label="Suche"
                >
                  <i className="ri-search-line text-lg text-hj-dark"></i>
                </button>
                <button
                  onClick={() => setMenuOpen(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer ml-1"
                  aria-label="Menü öffnen"
                >
                  <i className="ri-menu-line text-xl text-hj-dark"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Thin rule */}
          <div className="border-t border-hj-gray-border/60"></div>

          {/* Categories centered row */}
          <nav aria-label="Hauptnavigation">
            <div className="max-w-7xl mx-auto px-6">
              {catsLoading ? (
                <div className="flex items-center justify-center gap-6 py-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-3.5 w-16 bg-hj-gray-light rounded animate-pulse"></div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center overflow-x-auto scrollbar-hide">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`px-4 py-3 text-sm font-medium tracking-wide transition-all duration-200 whitespace-nowrap border-b-2 font-body ${
                        location.pathname === item.path
                          ? "text-hj-red border-hj-red"
                          : "text-hj-dark border-transparent hover:text-hj-red hover:border-hj-red/30"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* ─────────────────────────────────────────────
            COMPACT STATE (on scroll, desktop)
            Logo left | categories inline | actions right
        ───────────────────────────────────────────── */}
        <div
          className={`hidden lg:flex items-center overflow-hidden transition-all duration-500 ease-in-out ${
            scrolled ? "max-h-16 opacity-100" : "max-h-0 opacity-0"
          }`}
          aria-hidden={!scrolled}
        >
          <div className="w-full max-w-7xl mx-auto px-6 flex items-center gap-6 h-14">
            {/* Logo left — compact */}
            <div className="shrink-0">
              <Logo variant="header" />
            </div>

            {/* Vertical divider */}
            <div className="h-5 w-px bg-hj-gray-border shrink-0"></div>

            {/* Categories inline */}
            <nav className="flex-1 flex items-center overflow-hidden" aria-label="Hauptnavigation Kompakt">
              <div className="flex items-center overflow-x-auto scrollbar-hide gap-0">
                {navItems.slice(0, 8).map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-3 py-1 text-sm font-medium whitespace-nowrap transition-colors font-body ${
                      location.pathname === item.path
                        ? "text-hj-red font-semibold"
                        : "text-hj-dark hover:text-hj-red"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => setSearchOpen(true)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer"
                aria-label="Suche"
              >
                <i className="ri-search-line text-base text-hj-dark"></i>
              </button>
              <button
                onClick={() => setMenuOpen(true)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer"
                aria-label="Menü"
              >
                <i className="ri-menu-line text-lg text-hj-dark"></i>
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile Header ── */}
        <div className="lg:hidden">
          <div className="grid grid-cols-[48px_1fr_72px] items-center h-[52px] px-3 sm:px-4">
            {/* Left: logo icon only */}
            <div className="flex items-center justify-start">
              <Link to="/" className="block w-8 h-8 shrink-0">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <rect x="2" y="2" width="44" height="44" rx="8" stroke={settings.accentColor || "#8B0000"} strokeWidth="2.5" fill="none" />
                  <path d="M24 8 L24 20" stroke={settings.accentColor || "#8B0000"} strokeWidth="3" strokeLinecap="round" />
                  <path d="M24 28 L24 40" stroke={settings.accentColor || "#8B0000"} strokeWidth="3" strokeLinecap="round" />
                  <path d="M8 24 L20 24" stroke={settings.accentColor || "#8B0000"} strokeWidth="3" strokeLinecap="round" />
                  <path d="M28 24 L40 24" stroke={settings.accentColor || "#8B0000"} strokeWidth="3" strokeLinecap="round" />
                  <circle cx="24" cy="24" r="3" fill={settings.accentColor || "#8B0000"} />
                </svg>
              </Link>
            </div>
            {/* Center: title + slogan — perfectly centered */}
            <div className="flex items-center justify-center min-w-0">
              <Link to="/" className="text-center min-w-0">
                <h1 className="text-[17px] sm:text-[19px] font-bold text-hj-dark tracking-tight leading-tight truncate font-heading">
                  {settings.siteName}
                </h1>
                {settings.slogan && (
                  <p className="text-[10px] sm:text-[11px] text-hj-muted font-body mt-0.5 tracking-wide truncate">
                    {settings.slogan}
                  </p>
                )}
              </Link>
            </div>
            {/* Right: search + menu */}
            <div className="flex items-center justify-end gap-0.5">
              <button
                onClick={() => setSearchOpen(true)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer shrink-0"
                aria-label="Suche"
              >
                <i className="ri-search-line text-lg text-hj-dark"></i>
              </button>
              <button
                onClick={() => setMenuOpen(true)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer shrink-0"
                aria-label="Menü"
              >
                <i className="ri-menu-line text-lg text-hj-dark"></i>
              </button>
            </div>
          </div>

          {/* Mobile category scroll strip */}
          <div className={`border-t border-hj-gray-border transition-all duration-300 ${scrolled ? "opacity-0 max-h-0 overflow-hidden" : "h-[18px] max-h-[18px]"}`}>
            <div className="overflow-x-auto scrollbar-hide h-full flex items-center px-3 sm:px-4">
              <div className="flex items-center gap-1 w-max h-full">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-2 inline-flex items-center h-full text-[10px] leading-none font-medium whitespace-nowrap rounded-full transition-colors font-body ${
                      location.pathname === item.path
                        ? "text-hj-red bg-hj-red/10"
                        : "text-hj-dark hover:text-hj-red"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ─── Side Panel Menu ─── */}
      <SidePanel
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        navItems={navItems}
      />

      {/* ─── Full-Screen Search Overlay ─── */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-white/97 backdrop-blur-sm flex flex-col">
          <div className="flex items-center gap-3 px-4 md:px-8 py-4 border-b border-hj-gray-border">
            <i className="ri-search-line text-xl text-hj-muted shrink-0"></i>
            <form onSubmit={handleSearch} className="flex-1">
              <input
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Artikel, Themen oder Autoren suchen…"
                className="w-full text-base md:text-lg text-hj-dark placeholder-hj-muted bg-transparent focus:outline-none font-body"
                autoComplete="off"
              />
            </form>
            <button
              onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-hj-gray-light transition-colors cursor-pointer shrink-0"
              aria-label="Suche schließen"
            >
              <i className="ri-close-line text-xl text-hj-dark"></i>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
            {searchQuery.trim() ? (
              <div className="max-w-2xl mx-auto">
                <button
                  onClick={handleSearch}
                  className="w-full py-3.5 bg-hj-red text-white text-base font-medium rounded-lg hover:bg-hj-red-light transition-colors cursor-pointer"
                >
                  Suche nach &ldquo;{searchQuery}&rdquo;
                </button>
                <div className="mt-6">
                  <p className="text-xs font-bold uppercase tracking-widest text-hj-muted mb-3 font-body">
                    Nach Kategorie filtern
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {allCategories.slice(0, 8).map((cat) => (
                      <button
                        key={cat.slug}
                        onClick={() => {
                          navigate(`/suche?cat=${encodeURIComponent(cat.slug)}&q=${encodeURIComponent(searchQuery.trim())}`);
                          setSearchOpen(false);
                          setSearchQuery("");
                        }}
                        className="px-3 py-1.5 text-sm text-hj-red border border-hj-red/30 rounded-full hover:bg-hj-red hover:text-white transition-colors cursor-pointer"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto">
                <p className="text-xs font-bold uppercase tracking-widest text-hj-muted mb-4 font-body">
                  Beliebte Tags
                </p>
                <div className="flex flex-wrap gap-2">
                  {allTags.slice(0, 14).map((topic) => (
                    <button
                      key={topic}
                      onClick={() => {
                        navigate(`/suche?tag=${encodeURIComponent(topic)}`);
                        setSearchOpen(false);
                        setSearchQuery("");
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-hj-dark bg-hj-gray-light rounded-full hover:bg-hj-gray-border transition-colors cursor-pointer"
                    >
                      #{topic}
                    </button>
                  ))}
                </div>

                <p className="text-xs font-bold uppercase tracking-widest text-hj-muted mb-4 mt-8 font-body">
                  Kategorien
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {allCategories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => {
                        navigate(`/suche?cat=${encodeURIComponent(cat.slug)}`);
                        setSearchOpen(false);
                        setSearchQuery("");
                      }}
                      className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-hj-dark bg-hj-gray-light rounded-lg hover:bg-hj-gray-border transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-folder-line text-hj-red text-xs"></i>
                      {cat.name}
                    </button>
                  ))}
                </div>

                <p className="text-xs font-bold uppercase tracking-widest text-hj-muted mb-4 mt-8 font-body">
                  Schnellsuche
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Schweiz", "Finanzen", "Technologie", "Politik", "Gesundheit", "KI", "Deals", "Meinung"].map((topic) => (
                    <button
                      key={topic}
                      onClick={() => {
                        navigate(`/suche?q=${encodeURIComponent(topic)}`);
                        setSearchOpen(false);
                        setSearchQuery("");
                      }}
                      className="px-4 py-2 text-sm font-medium text-hj-red border border-hj-red/30 rounded-full hover:bg-hj-red hover:text-white transition-colors cursor-pointer"
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}